import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useDispatch } from "react-redux";
import { setAddMembersModalVisibility, setAddMembersMode } from "../../vcompSlice";
import Langs, { Localize } from "../../../../app/lang/langs";

/**
 * Generates a consistent color from a string (name).
 */
function stringToColor(str) {
    const colors = [
        "#FF7043", "#AB47BC", "#42A5F5", "#26A69A",
        "#EC407A", "#7E57C2", "#29B6F6", "#66BB6A",
        "#FFA726", "#5C6BC0", "#EF5350", "#78909C",
    ];
    let hash = 0;
    for (let i = 0; i < (str || "").length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
}

/**
 * Extracts initials from nom and prenom.
 */
function getInitials(nom, prenom) {
    const first = (prenom || "").charAt(0).toUpperCase();
    const last = (nom || "").charAt(0).toUpperCase();
    return `${first}${last}` || "?";
}

/**
 * MobileManagersTab - Mobile-first card-based managers list.
 * Replaces the desktop DataGrid with a touch-friendly list of manager cards.
 */
export default function MobileManagersTab({
    isMobile,
    mode,
    activeIndex,
    setActiveIndex,
    mangersList,
    dtManagers,
    onConfirmDeleteManager,
    onClose,
}) {
    const dispatch = useDispatch();

    const handleAddManager = () => {
        dispatch(setAddMembersModalVisibility(true));
        dispatch(setAddMembersMode("managers"));
    };

    const sectionCard = {
        bgcolor: "var(--surface-card, #fff)",
        borderRadius: "16px",
        p: 2.5,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    };

    return (
        <Fade in timeout={350}>
            <Box
                sx={{
                    mt: 0,
                    px: 2,
                    pb: "120px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minHeight: "100%",
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {/* ─── Header ─── */}
                <Box sx={sectionCard}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, color: "#333", fontSize: "17px" }}
                            >
                                <Langs str="EDIT_PROJECT_MANAGERS" />
                            </Typography>
                            <Chip
                                label={mangersList.length}
                                size="small"
                                sx={{
                                    bgcolor: "#FFF3E0",
                                    color: "#FF9015",
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    height: 26,
                                    minWidth: 26,
                                    "& .MuiChip-label": { px: 1 },
                                }}
                            />
                        </Box>
                        <IconButton
                            onClick={handleAddManager}
                            sx={{
                                bgcolor: "#FF9015",
                                color: "#fff",
                                width: 44,
                                height: 44,
                                boxShadow: "0 3px 12px rgba(255,144,21,0.35)",
                                "&:hover": { bgcolor: "#E8820F" },
                                "&:active": {
                                    transform: "scale(0.93)",
                                    boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                                },
                                transition: "all 0.15s ease-in-out",
                            }}
                        >
                            <PersonAddIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* ─── Managers List ─── */}
                {mangersList.length === 0 ? (
                    <Fade in timeout={400}>
                        <Box
                            sx={{
                                ...sectionCard,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: "50%",
                                    bgcolor: "#F5F5F5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 2,
                                }}
                            >
                                <GroupIcon sx={{ fontSize: 36, color: "#BDBDBD" }} />
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: "#888", mb: 0.5 }}
                            >
                                Aucun manager
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: "#BDBDBD", textAlign: "center", px: 2 }}
                            >
                                Ajoutez un manager pour gérer ce projet
                            </Typography>
                        </Box>
                    </Fade>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {mangersList.map((manager, index) => {
                            const fullName = `${manager.nom || ""} ${manager.prenom || ""}`.trim();
                            const avatarColor = stringToColor(fullName);
                            const initials = getInitials(manager.nom, manager.prenom);

                            return (
                                <Fade
                                    in
                                    timeout={300 + index * 80}
                                    key={manager.id}
                                >
                                    <Card
                                        elevation={0}
                                        sx={{
                                            borderRadius: "14px",
                                            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                                            bgcolor: "var(--surface-card, #fff)",
                                            border: "1px solid rgba(0,0,0,0.04)",
                                            transition: "all 0.2s ease",
                                            "&:active": {
                                                transform: "scale(0.985)",
                                                boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                                            <List disablePadding>
                                                <ListItem
                                                    disablePadding
                                                    sx={{ minHeight: 56 }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: avatarColor,
                                                                width: 44,
                                                                height: 44,
                                                                fontWeight: 700,
                                                                fontSize: "15px",
                                                                boxShadow: `0 2px 8px ${avatarColor}55`,
                                                            }}
                                                        >
                                                            {initials}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: "#333",
                                                                    fontSize: "15px",
                                                                    lineHeight: 1.3,
                                                                }}
                                                            >
                                                                {manager.prenom} {manager.nom}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: "#999", fontSize: "12px" }}
                                                            >
                                                                Manager
                                                            </Typography>
                                                        }
                                                    />
                                                    {mangersList.length > 1 && (
                                                        <ListItemSecondaryAction>
                                                            <IconButton
                                                                edge="end"
                                                                onClick={() => onConfirmDeleteManager(manager.id)}
                                                                sx={{
                                                                    color: "#BDBDBD",
                                                                    width: 44,
                                                                    height: 44,
                                                                    transition: "all 0.15s ease-in-out",
                                                                    "&:hover": {
                                                                        color: "#EF5350",
                                                                        bgcolor: "rgba(239,83,80,0.08)",
                                                                    },
                                                                    "&:active": {
                                                                        transform: "scale(0.9)",
                                                                        color: "#EF5350",
                                                                    },
                                                                }}
                                                            >
                                                                <DeleteOutlineIcon sx={{ fontSize: 22 }} />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    )}
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            );
                        })}
                    </Box>
                )}

                {/* ─── Sticky Bottom Navigation ─── */}
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2.5,
                        py: 2,
                        pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                        background: "linear-gradient(to top, rgba(255,255,255,0.92) 50%, rgba(255,255,255,0.78) 100%)",
                        backdropFilter: "blur(18px) saturate(1.6)",
                        WebkitBackdropFilter: "blur(18px) saturate(1.6)",
                        borderTop: "1px solid rgba(255, 144, 21, 0.15)",
                        boxShadow: "0 -4px 24px rgba(255, 144, 21, 0.06), 0 -1px 6px rgba(0, 0, 0, 0.04)",
                        zIndex: 10,
                        display: "flex",
                        gap: 1.5,
                    }}
                >
                    {mode !== "edit" && (
                        <Button
                            variant="outlined"
                            onClick={() => setActiveIndex(activeIndex - 1)}
                            startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "16px !important" }} />}
                            sx={{
                                flex: 1,
                                borderRadius: "14px",
                                minHeight: 50,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                letterSpacing: "-0.01em",
                                border: "1.5px solid rgba(0, 0, 0, 0.12)",
                                color: "#4b5263",
                                backgroundColor: "rgba(255, 255, 255, 0.6)",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                                transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                    borderColor: "rgba(255, 144, 21, 0.35)",
                                    backgroundColor: "rgba(255, 144, 21, 0.06)",
                                    color: "#FF9015",
                                },
                                "&:active": { transform: "scale(0.98)" },
                            }}
                        >
                            <Langs str="PREVIOUS" />
                        </Button>
                    )}
                    <Button
                        fullWidth={mode === "edit"}
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            flex: mode !== "edit" ? 1.5 : undefined,
                            borderRadius: "14px",
                            minHeight: 50,
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            letterSpacing: "-0.01em",
                            background: "linear-gradient(135deg, #FF9015 0%, #FFA940 100%)",
                            backgroundColor: "#FF9015",
                            color: "#fff",
                            border: "none",
                            boxShadow: "0 4px 16px rgba(255, 144, 21, 0.40), 0 8px 32px rgba(255, 144, 21, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                            transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #FF9015 0%, #FFB74D 100%)",
                                backgroundColor: "#FF9015",
                                boxShadow: "0 6px 24px rgba(255, 144, 21, 0.50), 0 12px 40px rgba(255, 144, 21, 0.25)",
                                transform: "translateY(-2px)",
                            },
                            "&:active": {
                                transform: "scale(0.98)",
                                boxShadow: "0 2px 10px rgba(255, 144, 21, 0.35)",
                            },
                        }}
                    >
                        <Langs str="Save & Exit" />
                    </Button>
                </Box>
            </Box>
        </Fade>
    );
}
